'use client'

/**
 * ContainerManager — the per-container CRUD UI. Used by:
 *   - /tags (customer-facing, workspace-scoped container)
 *   - /admin/tag-manager/[brandId] (admin, brand-scoped container)
 *
 * Caller picks the API base path (customers hit `/api/tags/[containerId]`,
 * admins hit `/api/admin/tag-manager/[brandId]`); component is agnostic.
 *
 * Spec: ~/.openclaw/workspace/specs/operation-sherman-spec.md §9
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import { HugeiconsIcon } from '@hugeicons/react'
import { PlusSignIcon, CodeIcon, Shield01Icon as ShieldIcon } from '@hugeicons-pro/core-stroke-rounded'
import { TagInstallPanel } from '@/components/admin/TagInstallPanel'
import type { ExampleContainerPayload } from '@/lib/developer/example-container-payload'

// ─── Types ───────────────────────────────────────────────────────────────────

interface Container {
  id: string
  brand_kit_id: string
  workspace_id: string
  published_version: number
  draft_version: number
  published_at: string | null
  updated_at: string
}

interface Tag {
  id: string
  container_id: string
  name: string
  type: string
  config: Record<string, unknown>
  trigger_ids: string[]
  priority: number
  enabled: boolean
  consent_category: string
  rate_limit_exempt: boolean
}

interface Trigger {
  id: string
  container_id: string
  name: string
  type: string
  config: Record<string, unknown>
  created_at: string
}

interface Variable {
  id: string
  container_id: string
  name: string
  type: string
  config: Record<string, unknown>
  created_at: string
}

interface Version {
  id: string
  version: number
  notes: string | null
  published_by: string
  created_at: string
}

type Section = 'tags' | 'triggers' | 'variables' | 'versions'

// ─── Constants ───────────────────────────────────────────────────────────────

const TAG_TYPES = ['pixel', 'script', 'custom_html', 'builtin'] as const
const TRIGGER_TYPES = ['pageview', 'click', 'custom_event', 'timer', 'scroll_depth', 'form_submit', 'element_visible'] as const
const VARIABLE_TYPES = ['constant', 'data_layer', 'cookie', 'dom_element', 'javascript', 'url_parameter', 'builtin'] as const
const CONSENT_CATEGORIES = ['necessary', 'analytics', 'marketing', 'preferences'] as const
const BUILTIN_TRACKERS = ['universal_ads', 'ga4', 'meta_pixel', 'tiktok_pixel', 'twitter_pixel', 'linkedin_insight', 'snapchat_pixel'] as const

const UA_EVENT_NAMES = [
  'page_view', 'purchase', 'add_to_cart', 'add_payment_info', 'start_checkout',
  'product_view', 'start_trial', 'sign_up', 'schedule_appt', 'subscribe',
  'content_view', 'search', 'view_cart', 'add_address_info', 'sign_in',
  'wish_list', 'custom_event_1', 'custom_event_2',
]

// ─── Small helpers ────────────────────────────────────────────────────────────

function TypeBadge({ type }: { type: string }) {
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-atrium-content text-atrium-muted capitalize">
      {type.replace(/_/g, ' ')}
    </span>
  )
}

function SectionLabel({ label, children }: { label: string; children?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-1.5">
      <label className="text-[10px] font-semibold text-atrium-muted uppercase tracking-wider">{label}</label>
      {children}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <SectionLabel label={label} />
      {children}
    </div>
  )
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

// ─── Config field renderers ───────────────────────────────────────────────────

function TagConfigFields({
  type,
  config,
  onChange,
}: {
  type: string
  config: Record<string, unknown>
  onChange: (key: string, value: unknown) => void
}) {
  if (type === 'pixel') {
    return (
      <Field label="Pixel URL">
        <Input
          value={(config.url as string) || ''}
          onChange={e => onChange('url', e.target.value)}
          placeholder="https://example.com/pixel.gif"
          className="h-8 text-sm"
        />
      </Field>
    )
  }
  if (type === 'script') {
    return (
      <Field label="Script Source URL">
        <Input
          value={(config.src as string) || ''}
          onChange={e => onChange('src', e.target.value)}
          placeholder="https://example.com/script.js"
          className="h-8 text-sm"
        />
      </Field>
    )
  }
  if (type === 'custom_html') {
    return (
      <Field label="HTML / Code">
        <Textarea
          value={(config.html as string) || ''}
          onChange={e => onChange('html', e.target.value)}
          placeholder="<script>...</script>"
          className="text-sm font-mono min-h-[120px]"
        />
      </Field>
    )
  }
  if (type === 'builtin') {
    return (
      <div className="space-y-3">
        <Field label="Tracker">
          <Select
            value={(config.tracker as string) || ''}
            onValueChange={v => onChange('tracker', v)}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="Select tracker..." />
            </SelectTrigger>
            <SelectContent>
              {BUILTIN_TRACKERS.map(t => (
                <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {config.tracker === 'universal_ads' && (
          <>
            <Field label="Event Name (blank = page_view)">
              <Select
                value={(config.event_name as string) || '__default__'}
                onValueChange={v => onChange('event_name', v === '__default__' ? undefined : v)}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="page_view (default)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">page_view (default)</SelectItem>
                  {UA_EVENT_NAMES.filter(n => n !== 'page_view').map(n => (
                    <SelectItem key={n} value={n}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Event Data (optional JSON)">
              <Textarea
                value={config.event_data ? JSON.stringify(config.event_data, null, 2) : ''}
                onChange={e => {
                  try {
                    const parsed = e.target.value ? JSON.parse(e.target.value) : undefined
                    onChange('event_data', parsed)
                  } catch { /* keep invalid JSON in textarea without saving */ }
                }}
                placeholder={'{\n  "value": 99.99,\n  "currency": "USD"\n}'}
                className="text-sm font-mono min-h-[80px]"
              />
            </Field>
            <p className="text-[11px] text-atrium-black/40">
              Pixel ID is workspace-scoped and injected automatically — no need to enter it here.
            </p>
          </>
        )}
      </div>
    )
  }
  return null
}

function TriggerConfigFields({
  type,
  config,
  onChange,
}: {
  type: string
  config: Record<string, unknown>
  onChange: (key: string, value: unknown) => void
}) {
  if (type === 'pageview') {
    return (
      <Field label="URL Pattern (leave blank for all pages)">
        <Input
          value={(config.url_pattern as string) || ''}
          onChange={e => onChange('url_pattern', e.target.value)}
          placeholder="/checkout/**"
          className="h-8 text-sm"
        />
      </Field>
    )
  }
  if (type === 'click') {
    return (
      <Field label="CSS Selector">
        <Input
          value={(config.selector as string) || ''}
          onChange={e => onChange('selector', e.target.value)}
          placeholder="button.cta, a[href*='checkout']"
          className="h-8 text-sm"
        />
      </Field>
    )
  }
  if (type === 'custom_event') {
    return (
      <Field label="Event Name">
        <Input
          value={(config.event_name as string) || ''}
          onChange={e => onChange('event_name', e.target.value)}
          placeholder="purchase"
          className="h-8 text-sm"
        />
      </Field>
    )
  }
  if (type === 'timer') {
    return (
      <Field label="Seconds">
        <Input
          type="number"
          value={(config.seconds as number) || 0}
          onChange={e => onChange('seconds', parseFloat(e.target.value) || 0)}
          placeholder="30"
          className="h-8 text-sm"
        />
      </Field>
    )
  }
  if (type === 'scroll_depth') {
    return (
      <Field label="Scroll Depth Threshold (%)">
        <Input
          type="number"
          value={(config.threshold as number) || 50}
          onChange={e => onChange('threshold', parseFloat(e.target.value) || 50)}
          placeholder="50"
          className="h-8 text-sm"
        />
      </Field>
    )
  }
  if (type === 'form_submit') {
    return (
      <Field label="Form CSS Selector">
        <Input
          value={(config.selector as string) || ''}
          onChange={e => onChange('selector', e.target.value)}
          placeholder="form#contact"
          className="h-8 text-sm"
        />
      </Field>
    )
  }
  if (type === 'element_visible') {
    return (
      <div className="space-y-3">
        <Field label="Element CSS Selector">
          <Input
            value={(config.selector as string) || ''}
            onChange={e => onChange('selector', e.target.value)}
            placeholder=".hero-section"
            className="h-8 text-sm"
          />
        </Field>
        <Field label="Visibility Threshold (0–1)">
          <Input
            type="number"
            step="0.1"
            min="0"
            max="1"
            value={(config.threshold as number) ?? 0.5}
            onChange={e => onChange('threshold', parseFloat(e.target.value) || 0.5)}
            placeholder="0.5"
            className="h-8 text-sm"
          />
        </Field>
      </div>
    )
  }
  return null
}

function VariableConfigFields({
  type,
  config,
  onChange,
}: {
  type: string
  config: Record<string, unknown>
  onChange: (key: string, value: unknown) => void
}) {
  if (type === 'constant') {
    return (
      <Field label="Value">
        <Input
          value={(config.value as string) || ''}
          onChange={e => onChange('value', e.target.value)}
          placeholder="my_constant_value"
          className="h-8 text-sm"
        />
      </Field>
    )
  }
  if (type === 'data_layer') {
    return (
      <Field label="Data Layer Key">
        <Input
          value={(config.key as string) || ''}
          onChange={e => onChange('key', e.target.value)}
          placeholder="ecommerce.value"
          className="h-8 text-sm"
        />
      </Field>
    )
  }
  if (type === 'cookie') {
    return (
      <Field label="Cookie Name">
        <Input
          value={(config.name as string) || ''}
          onChange={e => onChange('name', e.target.value)}
          placeholder="_ga"
          className="h-8 text-sm"
        />
      </Field>
    )
  }
  if (type === 'dom_element') {
    const waitMs = config.wait_ms == null ? '' : String(config.wait_ms)
    const transform = (config.transform as string) || ''
    return (
      <div className="space-y-3">
        <Field label="CSS Selector">
          <Input
            value={(config.selector as string) || ''}
            onChange={e => onChange('selector', e.target.value)}
            placeholder="#product-title"
            className="h-8 text-sm"
          />
        </Field>
        <Field label="Attribute (blank for text content)">
          <Input
            value={(config.attribute as string) || ''}
            onChange={e => onChange('attribute', e.target.value)}
            placeholder="data-id"
            className="h-8 text-sm"
          />
        </Field>
        <Field label="Wait for element (ms, blank = no wait)">
          <Input
            type="number"
            min={0}
            max={10000}
            step={100}
            value={waitMs}
            onChange={e => {
              const raw = e.target.value
              if (raw === '') { onChange('wait_ms', undefined); return }
              const n = Number(raw)
              if (!Number.isFinite(n)) return
              onChange('wait_ms', Math.max(0, Math.min(10000, Math.round(n))))
            }}
            placeholder="3000"
            className="h-8 text-sm"
          />
          <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
            Polls for the selector with MutationObserver before resolving.
            Use for late-mounted React/SPA content like checkout totals.
            Capped at 10000ms. Leave blank for instant lookup.
          </p>
        </Field>
        <Field label="Transform">
          <Select
            value={transform || '__none'}
            onValueChange={v => onChange('transform', v === '__none' ? undefined : v)}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">None (raw value)</SelectItem>
              <SelectItem value="numeric">Numeric (strip non-digits/.)</SelectItem>
              <SelectItem value="trim">Trim whitespace</SelectItem>
              <SelectItem value="lowercase">Lowercase</SelectItem>
              <SelectItem value="uppercase">Uppercase</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
            Applied after reading the value. <code>numeric</code> turns
            <code> $1,234.56</code> into <code>1234.56</code> — use for
            currency-formatted totals.
          </p>
        </Field>
      </div>
    )
  }
  if (type === 'javascript') {
    return (
      <Field label="JavaScript Expression">
        <Textarea
          value={(config.expression as string) || ''}
          onChange={e => onChange('expression', e.target.value)}
          placeholder="window.location.pathname"
          className="text-sm font-mono min-h-[80px]"
        />
      </Field>
    )
  }
  if (type === 'url_parameter') {
    return (
      <Field label="URL Parameter Name">
        <Input
          value={(config.param as string) || ''}
          onChange={e => onChange('param', e.target.value)}
          placeholder="utm_source"
          className="h-8 text-sm"
        />
      </Field>
    )
  }
  if (type === 'builtin') {
    return (
      <div className="text-sm text-atrium-muted bg-neutral-50 rounded-lg px-3 py-2">
        Built-in variables are provided automatically by the Atrium tag runtime (e.g. page URL, referrer, timestamp).
      </div>
    )
  }
  return null
}

// ─── Main page ────────────────────────────────────────────────────────────────

export interface ContainerManagerProps {
  /** Required: container id to manage. */
  containerId: string
  /**
   * Base API path — the customer surface uses `/api/tags/${containerId}` and
   * the admin surface uses `/api/admin/tag-manager/${brandId}`. Caller passes
   * the fully-resolved path (e.g. `/api/tags/abc-123`).
   */
  apiBase: string
  /** Optional display name (e.g. brand name, workspace name) for the header. */
  displayName?: string
  /** Optional header slot rendered to the left of the publish button. */
  headerSlot?: React.ReactNode
  /** Optional header slot replacement for the title (e.g. dropdown picker). */
  titleReplacement?: React.ReactNode
  /**
   * Hide the top header bar (title + status pills + publish button).
   * Stephen 2026-05-23: when used inside a host shell (e.g. /websites/[id])
   * the host owns the header chrome and surfaces Install/Block/Publish in
   * its own sidebar. Sidebar Install/Block/Publish stay visible.
   */
  hideHeader?: boolean
  /** Local-only demo mode for logged-out /developers preview. */
  exampleMode?: boolean
  examplePayload?: ExampleContainerPayload
  exampleInstallStatusUrl?: string
}

export function ContainerManager({
  containerId,
  apiBase,
  displayName,
  headerSlot,
  titleReplacement,
  hideHeader = false,
  exampleMode = false,
  examplePayload,
  exampleInstallStatusUrl = '/api/developer/example/container/install-status',
}: ContainerManagerProps) {
  const [loading, setLoading] = useState(true)
  const [publishing, setPublishing] = useState(false)
  const [creating, setCreating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [container, setContainer] = useState<Container | null>(null)
  const [tags, setTags] = useState<Tag[]>([])
  const [triggers, setTriggers] = useState<Trigger[]>([])
  const [variables, setVariables] = useState<Variable[]>([])
  const [versions, setVersions] = useState<Version[]>([])
  const [installSnippet, setInstallSnippet] = useState('')

  const [section, setSection] = useState<Section>('tags')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Local editor state
  const [editName, setEditName] = useState('')
  const [editType, setEditType] = useState('')
  const [editConfig, setEditConfig] = useState<Record<string, unknown>>({})
  const [editEnabled, setEditEnabled] = useState(true)
  const [editPriority, setEditPriority] = useState(0)
  const [editConsentCategory, setEditConsentCategory] = useState('necessary')
  const [editRateLimitExempt, setEditRateLimitExempt] = useState(false)
  const [editTriggerIds, setEditTriggerIds] = useState<string[]>([])

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isDirty = container
    ? container.draft_version > (container.published_version ?? 0)
    : false

  const installStatusUrl = exampleMode
    ? exampleInstallStatusUrl
    : `${apiBase}/install-status`

  const bumpDraft = useCallback(() => {
    setContainer(prev => {
      if (!prev) return prev
      return {
        ...prev,
        draft_version: prev.draft_version + 1,
        updated_at: new Date().toISOString(),
      }
    })
  }, [])

  // ── Fetch ──
  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const tagRes = await fetch(apiBase)
      const tagData = await tagRes.json()
      if (!tagRes.ok) throw new Error(tagData.error || 'Failed to load')

      setContainer(tagData.container)
      setTags(tagData.tags || [])
      setTriggers(tagData.triggers || [])
      setVariables(tagData.variables || [])
      setVersions(tagData.versions || [])
      setInstallSnippet(tagData.installSnippet || '')
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [apiBase])

  useEffect(() => {
    if (!exampleMode || !examplePayload) return
    setContainer(examplePayload.container as Container)
    setTags(examplePayload.tags as Tag[])
    setTriggers(examplePayload.triggers as Trigger[])
    setVariables(examplePayload.variables as Variable[])
    setVersions(examplePayload.versions as Version[])
    setInstallSnippet(examplePayload.installSnippet)
    setLoading(false)
  }, [exampleMode, examplePayload])

  useEffect(() => {
    if (exampleMode) return
    fetchData()
  }, [fetchData, exampleMode])

  // ── Select item ──
  const selectedTag = section === 'tags' ? tags.find(t => t.id === selectedId) : undefined
  const selectedTrigger = section === 'triggers' ? triggers.find(t => t.id === selectedId) : undefined
  const selectedVariable = section === 'variables' ? variables.find(v => v.id === selectedId) : undefined

  const populateEditor = useCallback((item: Tag | Trigger | Variable | null) => {
    if (!item) {
      setEditName('')
      setEditType('')
      setEditConfig({})
      setEditEnabled(true)
      setEditPriority(0)
      setEditConsentCategory('necessary')
      setEditRateLimitExempt(false)
      setEditTriggerIds([])
      return
    }
    setEditName(item.name)
    setEditType(item.type)
    setEditConfig({ ...(item.config || {}) })
    if ('enabled' in item) setEditEnabled(item.enabled)
    if ('priority' in item) setEditPriority(item.priority)
    if ('consent_category' in item) setEditConsentCategory(item.consent_category)
    if ('rate_limit_exempt' in item) setEditRateLimitExempt(item.rate_limit_exempt)
    if ('trigger_ids' in item) setEditTriggerIds(item.trigger_ids || [])
  }, [])

  useEffect(() => {
    if (section === 'tags') populateEditor(selectedTag || null)
    else if (section === 'triggers') populateEditor(selectedTrigger || null)
    else if (section === 'variables') populateEditor(selectedVariable || null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, section])

  const handleSelect = (id: string) => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    setSelectedId(id)
  }

  const handleSectionChange = (s: Section) => {
    setSection(s)
    setSelectedId(null)
  }

  // ── Save ──
  const save = useCallback(async (overrides?: {
    name?: string
    type?: string
    config?: Record<string, unknown>
    enabled?: boolean
    priority?: number
    consent_category?: string
    rate_limit_exempt?: boolean
    trigger_ids?: string[]
  }) => {
    if (!selectedId) return
    setSaving(true)
    try {
      const entity = section === 'tags' ? 'tag' : section === 'triggers' ? 'trigger' : 'variable'
      const updates: Record<string, unknown> = {
        name: overrides?.name ?? editName,
        type: overrides?.type ?? editType,
        config: overrides?.config ?? editConfig,
      }
      if (section === 'tags') {
        updates.enabled = overrides?.enabled ?? editEnabled
        updates.priority = overrides?.priority ?? editPriority
        updates.consent_category = overrides?.consent_category ?? editConsentCategory
        updates.rate_limit_exempt = overrides?.rate_limit_exempt ?? editRateLimitExempt
        updates.trigger_ids = overrides?.trigger_ids ?? editTriggerIds
      }

      if (exampleMode) {
        if (section === 'tags') {
          setTags(prev => prev.map(t => (t.id === selectedId ? { ...t, ...updates } as Tag : t)))
        } else if (section === 'triggers') {
          setTriggers(prev => prev.map(t => (t.id === selectedId ? { ...t, ...updates } as Trigger : t)))
        } else if (section === 'variables') {
          setVariables(prev => prev.map(v => (v.id === selectedId ? { ...v, ...updates } as Variable : v)))
        }
        bumpDraft()
        return
      }

      const res = await fetch(apiBase, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity, id: selectedId, ...updates }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)

      // Optimistic update
      if (section === 'tags' && data.tag) {
        setTags(prev => prev.map(t => t.id === selectedId ? data.tag : t))
      } else if (section === 'triggers' && data.trigger) {
        setTriggers(prev => prev.map(t => t.id === selectedId ? data.trigger : t))
      } else if (section === 'variables' && data.variable) {
        setVariables(prev => prev.map(v => v.id === selectedId ? data.variable : v))
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }, [selectedId, section, apiBase, editName, editType, editConfig, editEnabled, editPriority, editConsentCategory, editRateLimitExempt, editTriggerIds, exampleMode, bumpDraft])

  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => save(), 1000)
  }, [save])

  const handleBlurSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    save()
  }, [save])

  // ── Config field update ──
  const handleConfigChange = (key: string, value: unknown) => {
    setEditConfig(prev => {
      const next = { ...prev }
      // Treat undefined as "unset this key" — keeps the saved snapshot
      // clean (no JSON keys with explicit undefined/null when the author
      // clears a field).
      if (value === undefined) delete next[key]
      else next[key] = value
      if (saveTimer.current) clearTimeout(saveTimer.current)
      saveTimer.current = setTimeout(() => save({ config: next }), 1000)
      return next
    })
  }

  // ── Create ──
  const handleCreate = async () => {
    setCreating(true)
    setError(null)
    try {
      const action = section === 'tags' ? 'create_tag' : section === 'triggers' ? 'create_trigger' : 'create_variable'

      if (exampleMode) {
        const now = new Date().toISOString()
        if (section === 'tags') {
          const tag: Tag = {
            id: `example-tag-${Date.now()}`,
            container_id: containerId,
            name: 'New Tag',
            type: 'pixel',
            config: {},
            trigger_ids: [],
            priority: tags.length,
            enabled: true,
            consent_category: 'necessary',
            rate_limit_exempt: false,
          }
          setTags(prev => [...prev, tag])
          setSelectedId(tag.id)
          populateEditor(tag)
        } else if (section === 'triggers') {
          const trigger: Trigger = {
            id: `example-trigger-${Date.now()}`,
            container_id: containerId,
            name: 'New Trigger',
            type: 'pageview',
            config: {},
            created_at: now,
          }
          setTriggers(prev => [...prev, trigger])
          setSelectedId(trigger.id)
          populateEditor(trigger)
        } else {
          const variable: Variable = {
            id: `example-variable-${Date.now()}`,
            container_id: containerId,
            name: 'new_variable',
            type: 'constant',
            config: {},
            created_at: now,
          }
          setVariables(prev => [...prev, variable])
          setSelectedId(variable.id)
          populateEditor(variable)
        }
        bumpDraft()
        return
      }

      const res = await fetch(apiBase, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)

      if (section === 'tags' && data.tag) {
        setTags(prev => [...prev, data.tag])
        setSelectedId(data.tag.id)
        populateEditor(data.tag)
      } else if (section === 'triggers' && data.trigger) {
        setTriggers(prev => [...prev, data.trigger])
        setSelectedId(data.trigger.id)
        populateEditor(data.trigger)
      } else if (section === 'variables' && data.variable) {
        setVariables(prev => [...prev, data.variable])
        setSelectedId(data.variable.id)
        populateEditor(data.variable)
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Create failed')
    } finally {
      setCreating(false)
    }
  }

  // ── Delete ──
  const handleDelete = async () => {
    if (!selectedId || !confirm('Delete this item?')) return
    setDeleting(true)
    try {
      const entity = section === 'tags' ? 'tag' : section === 'triggers' ? 'trigger' : 'variable'

      if (exampleMode) {
        if (section === 'tags') setTags(prev => prev.filter(t => t.id !== selectedId))
        else if (section === 'triggers') setTriggers(prev => prev.filter(t => t.id !== selectedId))
        else setVariables(prev => prev.filter(v => v.id !== selectedId))
        setSelectedId(null)
        bumpDraft()
        return
      }

      const res = await fetch(apiBase, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity, id: selectedId }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)

      if (section === 'tags') setTags(prev => prev.filter(t => t.id !== selectedId))
      else if (section === 'triggers') setTriggers(prev => prev.filter(t => t.id !== selectedId))
      else if (section === 'variables') setVariables(prev => prev.filter(v => v.id !== selectedId))
      setSelectedId(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Delete failed')
    } finally {
      setDeleting(false)
    }
  }

  // ── Publish ──
  const handlePublish = async () => {
    setPublishing(true)
    setError(null)
    try {
      if (exampleMode) {
        const publishedAt = new Date().toISOString()
        setContainer(prev => {
          if (!prev) return prev
          const version = prev.draft_version
          setVersions(current => [
            {
              id: `example-version-${version}`,
              version,
              notes: null,
              published_by: 'example',
              created_at: publishedAt,
            },
            ...current,
          ])
          return {
            ...prev,
            published_version: version,
            published_at: publishedAt,
            updated_at: publishedAt,
          }
        })
        return
      }

      const res = await fetch(apiBase, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'publish' }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      await fetchData()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Publish failed')
    } finally {
      setPublishing(false)
    }
  }

  // ── Loading / error ──
  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 bg-atrium-content rounded" />
          <div className="flex gap-4 mt-4">
            <div className="w-48 h-96 bg-atrium-content rounded-xl" />
            <div className="w-72 h-96 bg-atrium-content rounded-xl" />
            <div className="flex-1 h-96 bg-atrium-content rounded-xl" />
          </div>
        </div>
      </div>
    )
  }

  if (error && !container) {
    return <div className="p-8 text-red-600">{error}</div>
  }

  const currentItems: Array<Tag | Trigger | Variable> =
    section === 'tags' ? tags :
    section === 'triggers' ? triggers :
    section === 'variables' ? variables : []

  const selectedItem = selectedTag || selectedTrigger || selectedVariable || null

  return (
    <div className="h-full flex flex-col overflow-hidden">

      {/* ── Header (suppressed when hideHeader=true; sidebar carries the
          status pills + publish button below). ── */}
      {!hideHeader && (
      <div className="shrink-0 flex items-center justify-between border-b border-atrium-border bg-atrium-background px-5 h-11">
        <div className="flex items-center gap-2 min-w-0">
          {titleReplacement ?? (
            <span className="text-sm font-semibold text-atrium-black truncate">
              {displayName || containerId}
            </span>
          )}
          {isDirty && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 shrink-0">
              Unpublished changes
            </span>
          )}
          {container?.published_version ? (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700 shrink-0">
              v{container.published_version} live
            </span>
          ) : null}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {error && (
            <span className="text-xs text-red-600">{error}</span>
          )}
          {headerSlot}
          <Button
            size="sm"
            onClick={handlePublish}
            disabled={publishing || !isDirty}
            className={cn(
              'h-7 text-xs font-medium transition-colors',
              isDirty
                ? 'bg-accent-600 hover:bg-accent-700 text-white'
                : 'bg-atrium-content text-atrium-muted cursor-not-allowed opacity-60',
            )}
            title={isDirty ? '' : 'No unpublished changes'}
          >
            {publishing
              ? 'Publishing...'
              : isDirty
                ? `Publish v${container?.draft_version ?? 1}`
                : `v${container?.published_version ?? 0} published`}
          </Button>
        </div>
      </div>
      )}

      {/* ── Three columns ── */}
      <div className="flex flex-1 min-h-0">

        {/* ── Left sidebar ── */}
        <div className="w-[200px] shrink-0 border-r border-atrium-border bg-atrium-background overflow-y-auto flex flex-col">
          <nav className="flex-1 p-2 space-y-0.5">
            {((['tags', 'triggers', 'variables', 'versions'] as Section[])).map(s => {
              const count = s === 'tags' ? tags.length : s === 'triggers' ? triggers.length : s === 'variables' ? variables.length : versions.length
              const label = s.charAt(0).toUpperCase() + s.slice(1)
              return (
                <button
                  key={s}
                  onClick={() => handleSectionChange(s)}
                  className={cn(
                    'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors',
                    section === s
                      ? 'bg-atrium-content text-atrium-black font-medium'
                      : 'text-atrium-muted hover:text-atrium-black hover:bg-atrium-content/60'
                  )}
                >
                  <span>{label}</span>
                  <span className={cn(
                    'text-xs px-1.5 py-0.5 rounded-full',
                    section === s ? 'bg-atrium-inner text-atrium-black' : 'bg-atrium-content text-atrium-black/50'
                  )}>
                    {count}
                  </span>
                </button>
              )
            })}
          </nav>

          {/* Sidebar footer: Install snippet + Block your tracking + Publish.
              Stephen 2026-05-23: split the single "View snippet & status"
              link into two discrete popovers and add the Publish button so
              the host shell can run with `hideHeader`. */}
          <div className="shrink-0 border-t border-atrium-border p-3 space-y-2">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-atrium-black transition-colors hover:bg-atrium-content"
                >
                  <HugeiconsIcon icon={CodeIcon} className="size-3.5 shrink-0 text-atrium-muted" />
                  Install snippet
                </button>
              </PopoverTrigger>
              <PopoverContent side="right" align="end" className="w-[440px] p-4 max-h-[80vh] overflow-y-auto">
                <TagInstallPanel
                  statusUrl={installStatusUrl}
                  installSnippet={installSnippet}
                  section="install"
                />
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-atrium-black transition-colors hover:bg-atrium-content"
                >
                  <HugeiconsIcon icon={ShieldIcon} className="size-3.5 shrink-0 text-atrium-muted" />
                  Block your tracking
                </button>
              </PopoverTrigger>
              <PopoverContent side="right" align="end" className="w-[440px] p-4 max-h-[80vh] overflow-y-auto">
                <TagInstallPanel
                  statusUrl={installStatusUrl}
                  installSnippet={installSnippet}
                  section="opt_out"
                />
              </PopoverContent>
            </Popover>
            <Button
              size="sm"
              onClick={handlePublish}
              disabled={publishing || !isDirty}
              className={cn(
                'w-full h-8 text-xs font-medium transition-colors',
                isDirty
                  ? 'bg-accent-600 hover:bg-accent-700 text-white'
                  : 'bg-atrium-content text-atrium-muted cursor-not-allowed opacity-60',
              )}
              title={isDirty ? '' : 'No unpublished changes'}
            >
              {publishing
                ? 'Publishing…'
                : isDirty
                  ? `Publish v${container?.draft_version ?? 1}`
                  : `v${container?.published_version ?? 0} published`}
            </Button>
          </div>
        </div>

        {/* ── Center: item list ── */}
        <div className="w-[300px] shrink-0 border-r border-atrium-border bg-atrium-content/30 overflow-y-auto flex flex-col">

          {/* List header */}
          {section !== 'versions' && (
            <div className="shrink-0 flex items-center justify-between px-4 py-2.5 border-b border-atrium-border bg-atrium-background">
              <span className="text-xs font-semibold text-atrium-muted uppercase tracking-wider capitalize">
                {section}
              </span>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="flex items-center gap-1 text-xs font-medium text-accent-600 hover:text-accent-700 disabled:opacity-50 transition-colors"
              >
                <HugeiconsIcon icon={PlusSignIcon} className="size-3.5" />
                New
              </button>
            </div>
          )}

          {/* Items */}
          <div className="flex-1 p-2 space-y-1">
            {section === 'versions' ? (
              versions.length === 0 ? (
                <div className="p-4 text-sm text-atrium-muted text-center">No published versions yet</div>
              ) : (
                versions.map(v => (
                  <div
                    key={v.id}
                    className="bg-atrium-inner border border-atrium-border rounded-lg p-3"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-atrium-black">v{v.version}</span>
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-50 text-green-700">
                        published
                      </span>
                    </div>
                    <div className="text-xs text-atrium-muted">{fmtDate(v.created_at)}</div>
                    {v.notes && <div className="text-xs text-atrium-muted mt-1 truncate">{v.notes}</div>}
                  </div>
                ))
              )
            ) : currentItems.length === 0 ? (
              <div className="p-4 text-sm text-atrium-muted text-center">
                No {section} yet. Click &ldquo;New&rdquo; to create one.
              </div>
            ) : (
              currentItems.map(item => {
                const isTag = 'enabled' in item
                const isSelected = item.id === selectedId
                return (
                  <button
                    key={item.id}
                    onClick={() => handleSelect(item.id)}
                    className={cn(
                      'w-full text-left bg-atrium-inner border rounded-lg p-3 transition-colors',
                      isSelected
                        ? 'border-accent-600/40 ring-1 ring-accent-600/20'
                        : 'border-atrium-border hover:border-atrium-border hover:bg-atrium-content/50'
                    )}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium text-atrium-black leading-tight truncate">
                          {item.name}
                        </span>
                        {isTag && item.name.startsWith('Universal Ads \u2014 ') && (
                          <span className="text-[10px] text-neutral-400 leading-tight mt-0.5">auto-installed</span>
                        )}
                      </div>
                      {isTag && (
                        <div className={cn(
                          'shrink-0 w-2 h-2 rounded-full mt-1',
                          (item as Tag).enabled ? 'bg-green-400' : 'bg-neutral-300'
                        )} />
                      )}
                    </div>
                    <TypeBadge type={item.type} />
                  </button>
                )
              })
            )}
          </div>
        </div>

        {/* ── Right: editor panel ── */}
        <div className="flex-1 overflow-y-auto bg-atrium-background">
          {!selectedItem || section === 'versions' ? (
            <div className="flex items-center justify-center h-full text-sm text-atrium-muted">
              {section === 'versions'
                ? 'Version history is shown in the list.'
                : `Select a ${section.slice(0, -1)} to edit it.`
              }
            </div>
          ) : (
            <div className="p-6 max-w-xl space-y-5">

              {/* Name + enabled */}
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <SectionLabel label="Name" />
                  <Input
                    value={editName}
                    onChange={e => { setEditName(e.target.value); scheduleSave() }}
                    onBlur={handleBlurSave}
                    className="h-8 text-sm font-medium"
                  />
                </div>
                {section === 'tags' && (
                  <div className="shrink-0 pt-5">
                    <Switch
                      checked={editEnabled}
                      onCheckedChange={v => {
                        setEditEnabled(v)
                        save({ enabled: v })
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Type */}
              <Field label="Type">
                <Select
                  value={editType}
                  onValueChange={v => {
                    setEditType(v)
                    setEditConfig({})
                    save({ type: v, config: {} })
                  }}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(section === 'tags' ? TAG_TYPES : section === 'triggers' ? TRIGGER_TYPES : VARIABLE_TYPES).map(t => (
                      <SelectItem key={t} value={t}>
                        {t.replace(/_/g, ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {/* Type-specific config */}
              {section === 'tags' && (
                <TagConfigFields
                  type={editType}
                  config={editConfig}
                  onChange={handleConfigChange}
                />
              )}
              {section === 'triggers' && (
                <TriggerConfigFields
                  type={editType}
                  config={editConfig}
                  onChange={handleConfigChange}
                />
              )}
              {section === 'variables' && (
                <VariableConfigFields
                  type={editType}
                  config={editConfig}
                  onChange={handleConfigChange}
                />
              )}

              {/* Tag-specific fields */}
              {section === 'tags' && (
                <>
                  {/* Triggers multi-select */}
                  <div>
                    <SectionLabel label="Triggers" />
                    {triggers.length === 0 ? (
                      <p className="text-xs text-atrium-muted">No triggers defined yet.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {triggers.map(tr => (
                          <label key={tr.id} className="flex items-center gap-2.5 cursor-pointer group">
                            <Checkbox
                              checked={editTriggerIds.includes(tr.id)}
                              onCheckedChange={checked => {
                                const next = checked
                                  ? [...editTriggerIds, tr.id]
                                  : editTriggerIds.filter(id => id !== tr.id)
                                setEditTriggerIds(next)
                                save({ trigger_ids: next })
                              }}
                            />
                            <span className="text-sm text-atrium-black">{tr.name}</span>
                            <TypeBadge type={tr.type} />
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Priority */}
                  <Field label="Priority (higher fires first)">
                    <Input
                      type="number"
                      value={editPriority}
                      onChange={e => { setEditPriority(parseInt(e.target.value) || 0); scheduleSave() }}
                      onBlur={handleBlurSave}
                      className="h-8 text-sm w-24"
                    />
                  </Field>

                  {/* Consent category */}
                  <Field label="Consent Category">
                    <Select
                      value={editConsentCategory}
                      onValueChange={v => {
                        setEditConsentCategory(v)
                        save({ consent_category: v })
                      }}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONSENT_CATEGORIES.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  {/* Rate limit exempt */}
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={editRateLimitExempt}
                      onCheckedChange={v => {
                        setEditRateLimitExempt(v)
                        save({ rate_limit_exempt: v })
                      }}
                    />
                    <div>
                      <div className="text-sm text-atrium-black font-medium">Rate limit exempt</div>
                      <div className="text-xs text-atrium-muted">Fire this tag even when rate limiting is active</div>
                    </div>
                  </div>
                </>
              )}

              {/* Save + Delete */}
              <div className="flex items-center justify-between pt-2 border-t border-atrium-border">
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="text-xs text-red-500 hover:text-red-600 disabled:opacity-50 transition-colors"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={() => save()}
                  disabled={saving}
                  className="flex items-center gap-1.5 bg-accent-600 hover:bg-accent-700 text-white text-xs font-medium px-4 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>

            </div>
          )}
        </div>

      </div>
    </div>
  )
}
